/**
 * The views of the catalog screens, as the address bar holds them. A default
 * is not written into the address.
 */
import { idOf, marketOf, pageOf, text } from "@/shared/lib/search";

import type { AreaPricesFilter, ServicesFilter } from "../api/queries";

/** Which services a list shows: those on show, those switched off, or the deleted ones too. */
export const SHOWN = ["active", "off", "deleted"] as const;
export type Shown = (typeof SHOWN)[number];

export interface ServicesSearch {
  page?: number;
  /** Part of the name, in any language. */
  q?: string;
  category?: string;
  profession?: string;
  show?: Shown;
}

/** Reads the address. Anything it does not know is dropped, never trusted. */
export function parseServicesSearch(raw: Readonly<Record<string, unknown>>): ServicesSearch {
  const search: ServicesSearch = {};
  const page = pageOf(raw["page"]);
  if (page !== undefined) search.page = page;
  const q = text(raw["q"]);
  if (q !== undefined) search.q = q;
  const category = idOf(raw["category"]);
  if (category !== undefined) search.category = category;
  const profession = idOf(raw["profession"]);
  if (profession !== undefined) search.profession = profession;
  const show = SHOWN.find((known) => known === raw["show"]);
  if (show !== undefined) search.show = show;
  return search;
}

/** What the list asks `GET /v2/admin/services` for. */
export function servicesFilter(search: ServicesSearch): ServicesFilter {
  const filter: ServicesFilter = {};
  if (search.q !== undefined) filter.q = search.q;
  if (search.category !== undefined) filter.category_id = search.category;
  if (search.profession !== undefined) filter.profession_id = search.profession;
  if (search.show === "active") filter.is_active = true;
  if (search.show === "off") filter.is_active = false;
  if (search.show === "deleted") filter.deleted = true;
  return filter;
}

/** The view after a filter changed: its first page. */
export function withServiceFilters(filters: {
  q?: string | undefined;
  category?: string | undefined;
  profession?: string | undefined;
  show?: string | undefined;
}): ServicesSearch {
  const next: ServicesSearch = {};
  if (filters.q !== undefined) next.q = filters.q;
  if (filters.category !== undefined) next.category = filters.category;
  if (filters.profession !== undefined) next.profession = filters.profession;
  const show = SHOWN.find((known) => known === filters.show);
  if (show !== undefined) next.show = show;
  return next;
}

/** The same view on another page; the first page is not written. */
export function withPage<Search extends { page?: number }>(search: Search, page: number): Search {
  const { page: _, ...rest } = search;
  return (page > 1 ? { ...rest, page } : rest) as Search;
}

// ---- the reference lists ----

/** The lists of the reference screen, a tab each. */
export const LISTS = [
  "categories",
  "age_groups",
  "cancel_reasons",
  "rating_tags",
  "relationships",
] as const;
export type ListId = (typeof LISTS)[number];
export const DEFAULT_LIST: ListId = "categories";

export interface ListsSearch {
  list?: ListId;
}

export function parseListsSearch(raw: Readonly<Record<string, unknown>>): ListsSearch {
  const list = LISTS.find((known) => known === raw["list"]);
  return list === undefined || list === DEFAULT_LIST ? {} : { list };
}

export function listOf(search: ListsSearch): ListId {
  return search.list ?? DEFAULT_LIST;
}

export function withList(list: ListId): ListsSearch {
  return list === DEFAULT_LIST ? {} : { list };
}

// ---- the professions ----

export interface ProfessionsSearch {
  /** The profession shown beside the list; none is the first of it. */
  profession?: string;
}

export function parseProfessionsSearch(raw: Readonly<Record<string, unknown>>): ProfessionsSearch {
  const profession = idOf(raw["profession"]);
  return profession === undefined ? {} : { profession };
}

// ---- the price desk ----

/** The views of the price desk: what things cost in the areas, the call-out fees, the platform's share. */
export const PRICE_VIEWS = ["sheet", "fees", "commission"] as const;
export type PriceView = (typeof PRICE_VIEWS)[number];
export const DEFAULT_PRICE_VIEW: PriceView = "sheet";

export interface PricesSearch {
  view?: PriceView;
  /** One area's prices; none: every area. */
  area?: string;
  /** One category's services. */
  category?: string;
  /** One service, across its areas. */
  service?: string;
  /** One market's areas. */
  market?: string;
  /** The page of what things cost. */
  page?: number;
  /** What ended is shown too (the fees, the rules). */
  history?: boolean;
}

export function parsePricesSearch(raw: Readonly<Record<string, unknown>>): PricesSearch {
  const search: PricesSearch = {};
  const view = PRICE_VIEWS.find((known) => known === raw["view"]);
  if (view !== undefined && view !== DEFAULT_PRICE_VIEW) search.view = view;
  const area = idOf(raw["area"]);
  if (area !== undefined) search.area = area;
  const category = idOf(raw["category"]);
  if (category !== undefined) search.category = category;
  const service = idOf(raw["service"]);
  if (service !== undefined) search.service = service;
  const market = marketOf(raw["market"]);
  if (market !== undefined) search.market = market;
  const page = pageOf(raw["page"]);
  if (page !== undefined) search.page = page;
  if (raw["history"] === true || raw["history"] === "true") search.history = true;
  return search;
}

export function priceViewOf(search: PricesSearch): PriceView {
  return search.view ?? DEFAULT_PRICE_VIEW;
}

/** The filters of what things cost: kept from one view to another. */
const SHEET_FILTERS = ["area", "category", "service", "market"] as const;

/** Another view of the desk: the filters chosen are kept; the page and the history are a view's own. */
export function withPriceView(search: PricesSearch, view: PriceView): PricesSearch {
  const next: PricesSearch = {};
  if (view !== DEFAULT_PRICE_VIEW) next.view = view;
  for (const name of SHEET_FILTERS) {
    const value = search[name];
    if (value !== undefined) next[name] = value;
  }
  return next;
}

/** The view after a filter of what things cost changed: its first page. */
export function withPriceFilters(
  search: PricesSearch,
  filters: Partial<Record<(typeof SHEET_FILTERS)[number], string | undefined>>,
): PricesSearch {
  const { page: _, area: __, category: ___, service: ____, market: _____, ...rest } = search;
  const next: PricesSearch = { ...rest };
  for (const name of SHEET_FILTERS) {
    const value = filters[name];
    if (value !== undefined) next[name] = value;
  }
  return next;
}

/** What `GET /v2/admin/area-prices` is asked for. */
export function areaPricesFilter(search: PricesSearch): AreaPricesFilter {
  const filter: AreaPricesFilter = {};
  if (search.area !== undefined) filter.area_id = search.area;
  if (search.category !== undefined) filter.category_id = search.category;
  if (search.service !== undefined) filter.service_id = search.service;
  if (search.market !== undefined) filter.market = search.market;
  return filter;
}

// ---- the areas ----

export interface AreasSearch {
  /** The area that is chosen. */
  area?: string;
}

export function parseAreasSearch(raw: Readonly<Record<string, unknown>>): AreasSearch {
  const area = idOf(raw["area"]);
  return area === undefined ? {} : { area };
}
